/**
 * Client-side point-address checks, one per driver, mirroring the platform's
 * rules so a hand-typed or imported point is refused before go-live rather
 * than by the server:
 *
 * - the driver registry's address schema (platform-core/internal/iot/drivers/
 *   schemas/*.address.json): required fields, integer fields, ranges, enums;
 * - the contract's strict decode + Validate() (platform-core/pkg/protocol/
 *   driver.go: ModbusAddress, OPCUAAddress, S7Address, ENIPAddress,
 *   MELSECAddress): unknown fields rejected, and the cross-field rules a
 *   schema cannot say (an S7 DB needs db >= 1, db only for area DB, the MELSEC
 *   device-code tables and hex/decimal numbering, the OPC UA node id shape).
 *
 * Messages follow the Go ones. The server stays the authority: go-live still
 * surfaces its field errors per point.
 */

import { DRIVER, isModbusDriver } from "./drivers";

/** The address fields each driver's address takes (the strict decode rejects any other). */
export const ADDRESS_FIELDS: Record<string, readonly string[]> = {
  modbus: ["space", "register", "count", "word_order"],
  [DRIVER.OPCUA]: ["node_id"],
  [DRIVER.S7]: ["area", "db", "byte", "bit"],
  [DRIVER.ENIP]: ["tag", "elem"],
  [DRIVER.MELSEC]: ["device", "word_order"],
  [DRIVER.RESULTS_FEED]: ["key"],
};

/** The address fields of a driver (the two Modbus drivers share one shape); empty for an unknown driver. */
export function addressFieldsOf(driver: string): readonly string[] {
  return ADDRESS_FIELDS[isModbusDriver(driver) ? "modbus" : driver] ?? [];
}

export const MODBUS_SPACES = ["holding", "input", "coil", "discrete"] as const;
export const WORD_ORDERS = ["big", "little", "cdab", "badc"] as const;
export const S7_AREAS = ["DB", "M", "I", "Q"] as const;

// MELSEC device codes (driver.go melsecHexDevices / melsecDecDevices).
const MELSEC_HEX = new Set(["X", "Y", "B", "W", "SB", "SW", "DX", "DY"]);
const MELSEC_DEC = new Set(["D", "M", "L", "F", "V", "R", "ZR", "SM", "SD", "TN", "CN", "TS", "CS"]);

const isInt = (v: unknown): v is number => typeof v === "number" && Number.isInteger(v);

/** A present field that is not an integer (Go's decode of a string or 1.5 into an int fails). */
function notInt(a: Record<string, unknown>, k: string): boolean {
  return a[k] !== undefined && a[k] !== null && !isInt(a[k]);
}

function wordOrderError(where: string, wo: unknown): string | null {
  if (wo === undefined || wo === null || wo === "") return null;
  if (typeof wo !== "string" || !(WORD_ORDERS as readonly string[]).includes(wo)) {
    return `${where}: unknown word_order ${JSON.stringify(wo)}`;
  }
  return null;
}

export function validateModbusAddress(a: Record<string, unknown>): string | null {
  const where = "modbus address";
  if (a.space !== undefined && a.space !== "" && !(MODBUS_SPACES as readonly unknown[]).includes(a.space)) {
    return `${where}: unknown space ${JSON.stringify(a.space)}`;
  }
  if (a.register === undefined || a.register === null || a.register === "") return `${where}: register is required`;
  if (notInt(a, "register")) return `${where}: register must be a whole number`;
  const register = a.register as number;
  if (register < 0 || register > 65535) return `${where}: register must be 0..65535`;
  if (notInt(a, "count")) return `${where}: count must be a whole number`;
  const count = (a.count as number | undefined) ?? 0;
  if (count < 0 || count > 4) return `${where}: count must be 1..4`;
  return wordOrderError(where, a.word_order);
}

/** OPCUAAddress.Validate: optional ns=/nsu= prefix, then i=, s=, g= or b= with an identifier. */
export function validateOpcuaAddress(a: Record<string, unknown>): string | null {
  const raw = a.node_id;
  if (typeof raw !== "string" || raw === "") return "opcua address: node_id is required";
  let id = raw;
  if (id.startsWith("nsu=") || id.startsWith("ns=")) {
    const semi = id.indexOf(";");
    if (semi < 0 || semi === id.length - 1) {
      return `opcua address: node_id ${JSON.stringify(raw)} needs an identifier after ';'`;
    }
    id = id.slice(semi + 1);
  }
  if (id.length < 3 || id[1] !== "=" || !"isgb".includes(id[0])) {
    return `opcua address: node_id ${JSON.stringify(raw)} must end in i=, s=, g= or b=<id>`;
  }
  return null;
}

export function validateS7Address(a: Record<string, unknown>): string | null {
  const where = "s7 address";
  if (!(S7_AREAS as readonly unknown[]).includes(a.area)) return `${where}: unknown area ${JSON.stringify(a.area ?? "")}`;
  for (const k of ["db", "byte", "bit"]) if (notInt(a, k)) return `${where}: ${k} must be a whole number`;
  const db = (a.db as number | undefined) ?? 0;
  if (a.area === "DB" && db < 1) return `${where}: area DB needs db >= 1`;
  if (a.area !== "DB" && db !== 0) return `${where}: db is only valid for area DB`;
  if (a.byte === undefined || a.byte === null) return `${where}: byte is required`;
  if ((a.byte as number) < 0) return `${where}: byte must be >= 0`;
  if (a.bit !== undefined && a.bit !== null && ((a.bit as number) < 0 || (a.bit as number) > 7)) {
    return `${where}: bit must be 0..7`;
  }
  return null;
}

export function validateEnipAddress(a: Record<string, unknown>): string | null {
  if (typeof a.tag !== "string" || a.tag.trim() === "") return "enip address: tag is required";
  if (notInt(a, "elem")) return "enip address: elem must be a whole number";
  if (a.elem !== undefined && a.elem !== null && (a.elem as number) < 0) return "enip address: elem must be >= 0";
  return null;
}

/**
 * ParseMELSECDevice: letters then digits; a hex-numbered one-letter code may
 * swallow a leading hex digit ("YA0" is Y 0xA0), a real two-letter code wins;
 * the digits must be in the code's base and fit 32 bits.
 */
export function parseMelsecDevice(dev: string): { code: string; number: number } | { error: string } {
  const m = /^([A-Z]{1,2})([0-9A-F]+)$/.exec(dev.trim().toUpperCase());
  if (!m) return { error: `melsec address: device ${JSON.stringify(dev)} is not a MELSEC device (e.g. D100, X1F)` };
  let letters = m[1];
  let digits = m[2];
  if (!MELSEC_HEX.has(letters) && !MELSEC_DEC.has(letters)) {
    if (letters.length === 2 && MELSEC_HEX.has(letters[0])) {
      digits = letters.slice(1) + digits;
      letters = letters[0];
    } else {
      return { error: `melsec address: unknown device code ${JSON.stringify(letters)}` };
    }
  }
  const base = MELSEC_HEX.has(letters) ? 16 : 10;
  let n = 0;
  for (const c of digits) {
    const d = "0123456789ABCDEF".indexOf(c);
    if (d < 0 || d >= base) {
      return { error: `melsec address: ${JSON.stringify(digits)} is not a base-${base} number for device ${letters}` };
    }
    n = n * base + d;
    if (n > 0xffffffff) return { error: `melsec address: device number ${JSON.stringify(digits)} out of range` };
  }
  return { code: letters, number: n };
}

export function validateMelsecAddress(a: Record<string, unknown>): string | null {
  if (typeof a.device !== "string" || a.device === "") return "melsec address: device is required";
  const parsed = parseMelsecDevice(a.device);
  if ("error" in parsed) return parsed.error;
  return wordOrderError("melsec address", a.word_order);
}

/** A results-feed value key (ResultsFeedAddress): a lower-case letter, then letters, digits or underscores, as the contract checks it. */
export function validateResultsFeedAddress(a: Record<string, unknown>): string | null {
  if (typeof a.key !== "string" || !/^[a-z][a-z0-9_]{0,63}$/.test(a.key)) {
    return `results-feed address: key ${JSON.stringify(a.key ?? "")} must start with a lower-case letter, then lower-case letters, digits or underscores`;
  }
  return null;
}

/**
 * One point address for a driver: null when the platform would accept it,
 * else the reason. Fields outside the driver's shape are refused, as the
 * contract's strict decode does.
 */
export function validateAddress(driver: string, address: Record<string, unknown> | null | undefined): string | null {
  if (!address || typeof address !== "object") return "address is missing";
  const fields = addressFieldsOf(driver);
  if (fields.length === 0) return `unknown driver ${JSON.stringify(driver)}`;
  const extra = Object.keys(address).find((k) => !fields.includes(k));
  if (extra) return `${isModbusDriver(driver) ? "modbus" : driver} address: unknown field ${JSON.stringify(extra)}`;
  if (isModbusDriver(driver)) return validateModbusAddress(address);
  switch (driver) {
    case DRIVER.OPCUA:
      return validateOpcuaAddress(address);
    case DRIVER.S7:
      return validateS7Address(address);
    case DRIVER.ENIP:
      return validateEnipAddress(address);
    case DRIVER.MELSEC:
      return validateMelsecAddress(address);
    case DRIVER.RESULTS_FEED:
      return validateResultsFeedAddress(address);
  }
  return null;
}
